import { Tile } from "@elion-health/ui";
import { CoverageBar } from "./CoverageBar";
import { CoverageSwatch } from "./CoverageLegend";
import {
  COVERAGE_LABEL,
  COVERAGE_MEANING,
  COVERAGE_STATES,
  type CoverageCounts,
  coverageTotal,
} from "./coverage";

export interface CoverageSummaryBarProps {
  coverage: CoverageCounts;
  /** The phrase after the step count, e.g. "workflow steps in Pharmacy". */
  noun: string;
  /** The explanatory line under the count. */
  description?: string;
}

/**
 * A headline read of step coverage: the step count, one stacked bar, and a
 * per-state count with what each state means.
 */
export function CoverageSummaryBar({
  coverage,
  noun,
  description = "A workflow step is a single task in a workflow, like verifying an order or dispensing a dose.",
}: CoverageSummaryBarProps) {
  return (
    <Tile border padding="none" className="bg-(--bg-white) p-4 sm:p-5">
      <p className="flex flex-wrap items-baseline gap-x-2">
        <span className="text-[22px] font-semibold tabular-nums tracking-tight text-(--fg-primary)">
          {coverageTotal(coverage)}
        </span>
        <span className="text-[13px] font-medium text-(--fg-secondary)">
          {noun}
        </span>
      </p>
      <p className="mt-1! text-[12px] leading-relaxed text-(--fg-muted)!">
        {description}
      </p>
      <div className="mt-3">
        <CoverageBar coverage={coverage} size="lg" labelled={false} />
      </div>
      <dl className="mt-3 grid grid-cols-1 gap-x-5 gap-y-3 sm:grid-cols-3">
        {COVERAGE_STATES.map((state) => (
          <div key={state}>
            <dt className="flex items-center gap-1.5">
              <CoverageSwatch state={state} />
              <span className="text-[14px] font-semibold tabular-nums text-(--fg-primary)">
                {coverage[state]}
              </span>
              <span className="text-[12px] font-medium text-(--fg-secondary)">
                {COVERAGE_LABEL[state]}
              </span>
            </dt>
            <dd className="mt-0.5 text-[11px] leading-snug text-(--fg-muted)">
              {COVERAGE_MEANING[state]}
            </dd>
          </div>
        ))}
      </dl>
    </Tile>
  );
}
